import React, { useMemo, useId, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { generateTicks } from '../lib/ticks';
import { valueToPx } from '../lib/scale';
import { fractionToDecimal } from '../lib/scaleFactor';
import { determineSecondaryDecimalPlaces, formatSecondaryValue } from '../lib/secondaryLineFormatting';
import { getThemeColors } from '../lib/themeColors';
import MathRenderer from './MathRenderer';
import type { Fraction, Domain, LabelVisibilityState, SubdivisionMode } from '../types/redux';
import type { LocaleCode } from '../lib/locale';

export interface SecondaryLineLayerProps {
  enabled: boolean;
  isHorizontal: boolean;
  width: number;
  height: number;
  lineX: number;
  lineY: number;
  scale: number;
  domain: Domain;
  unitInterval: Fraction;
  scaleFactor: Fraction;
  numberType: 'integer' | 'decimal' | 'fraction' | 'percent';
  fractionDisplay: { format: 'mixed' | 'improper'; simplify: boolean };
  locale: LocaleCode;
  hideAllLabels: boolean;
  labelVisibility: LabelVisibilityState;
  onLabelClick: (valueKey: string, e: React.MouseEvent) => void;
  subdivisionMode?: SubdivisionMode;
}

const BUFFER_VIEWPORTS = 1.5; // render ±1.5 viewports for stability while panning

export const SecondaryLineLayer: React.FC<SecondaryLineLayerProps> = React.memo((props) => {
  const {
    enabled,
    isHorizontal,
    width,
    height,
    lineX,
    lineY,
    scale,
    domain,
    unitInterval,
    scaleFactor,
    numberType,
    fractionDisplay,
    locale,
    hideAllLabels,
    labelVisibility,
    onLabelClick,
    subdivisionMode = 'smart',
  } = props;

  const clipId = useId();
  
  // Dark mode support
  const preferences = useSelector((state: RootState) => state.preferences);
  const isDark = preferences.theme === 'dark';
  const themeColors = useMemo(() => getThemeColors(isDark), [isDark]);

  // DNL: Select DNL tools state for sparse mode filtering
  const dnlTools = useSelector((state: RootState) => state.dnlTools);
  const sparseModeEnabled = useSelector((state: RootState) => state.dnlTools.sparseMode.enabled);
  const showZeroInSparse = useSelector((state: RootState) => state.dnlTools.sparseMode.showZero);

  const lineLength = isHorizontal ? width : height;

  // Do NOT return early before hooks; keep hooks order stable across renders

  // Buffered domain anchored to the current visible domain (follows pan/zoom)
  const bufferedDomain: Domain = useMemo(() => {
    const unitsInViewport = lineLength / scale;
    const bufferUnits = unitsInViewport * BUFFER_VIEWPORTS;
    return {
      start: domain.start - bufferUnits,
      end: domain.end + bufferUnits,
    };
  }, [domain.start, domain.end, lineLength, scale]);

  // Generate ticks once for buffered domain - using same subdivision mode as primary
  const ticks = useMemo(() => {
    let effectiveNumberType = numberType;
    let forceIntegerLabels = false;

    if (numberType === 'integer' && (unitInterval.n !== 1 || unitInterval.d !== 1)) {
      effectiveNumberType = 'decimal';
      forceIntegerLabels = false;
    }

    return generateTicks(
      bufferedDomain,
      unitInterval,
      scale,
      lineLength,
      {
        fractionDisplay,
        numberType: effectiveNumberType,
        forceIntegerLabels,
        customTicks: [],
        showSubticks: true,
        minPxSubtick: 8,
        maxSubdivisions: 4,
        subdivisionMode, // Match the primary line's subdivision mode
      },
      locale
    );
  }, [bufferedDomain.start, bufferedDomain.end, unitInterval.n, unitInterval.d, scale, lineLength, fractionDisplay.format, fractionDisplay.simplify, numberType, locale, subdivisionMode]);

  // Filter ticks based on sparse mode (mirroring primary line logic)
  const filteredTicks = useMemo(() => {
    return ticks.filter(tick => {
      // DNL: In sparse mode, hide regular ticks unless they match a custom tick or are zero
      if (sparseModeEnabled && tick.source !== 'custom' && tick.source !== 'midpoint') {
        // Always show zero if configured
        if (showZeroInSparse && Math.abs(tick.value) < 1e-10) {
          return true;
        }
        // Check if this value matches any DNL custom tick for secondary line
        const matchesDNLTick = dnlTools.sparseMode.customTicks.some(
          dnlTick => Math.abs(dnlTick.value - tick.value) < 1e-10 && 
                     (dnlTick.line === 'secondary' || dnlTick.line === 'both') &&
                     dnlTick.showTick
        );
        if (!matchesDNLTick) {
          return false; // Hide this tick in sparse mode
        }
      }
      
      return true;
    });
  }, [ticks, sparseModeEnabled, showZeroInSparse, dnlTools.sparseMode.customTicks]);

  const majorTicks = useMemo(() => filteredTicks.filter(t => t.major), [filteredTicks]);
  const minorTicks = useMemo(() => filteredTicks.filter(t => !t.major), [filteredTicks]);

  // Label formatting
  const scaleFactorDecimal = useMemo(() => fractionToDecimal(scaleFactor), [scaleFactor]);
  // For secondary line, the scale factor is not unitInterval but user preference; callsite passes numberType and fractionDisplay. Here we compute decimal places for base major ticks
  const decimalPlaces = useMemo(() => {
    if (numberType === 'decimal') {
      const values = majorTicks.map(t => t.value);
      return determineSecondaryDecimalPlaces(values, scaleFactorDecimal);
    }
    return 2;
  }, [majorTicks, numberType, scaleFactorDecimal]);

  const getLabel = useCallback((value: number): string => {
    return formatSecondaryValue(
      value,
      scaleFactorDecimal,
      numberType,
      fractionDisplay,
      locale,
      decimalPlaces
    );
  }, [numberType, fractionDisplay, locale, decimalPlaces, scaleFactorDecimal]);

  const textScale = preferences.textScale ?? 1;
  const minorLabelBoxWidth = (isHorizontal ? 50 : 60) * textScale + 10;
  const minorLabelBoxHeight = 24 * textScale + 6;
  const majorLabelBoxWidth = (isHorizontal ? 70 : 80) * textScale + 10;
  const majorLabelBoxHeight = (isHorizontal ? 32 : 28) * textScale + 8;

  const ySecondary = isHorizontal ? lineY - 120 : lineY;
  const xSecondary = isHorizontal ? lineX : lineX + 140; // Wider gap in vertical mode

  // Match primary orientation: in vertical mode, values increase upward
  const valueToPixel = useCallback((value: number): number => {
    const base = valueToPx(value, domain, lineLength);
    return isHorizontal ? base : lineLength - base;
  }, [domain.start, domain.end, lineLength, isHorizontal]);

  // Draw the axis across the full viewport width/height; clipping will keep it tidy
  const secLineStartX = isHorizontal ? 0 : xSecondary;
  const secLineStartY = isHorizontal ? ySecondary : 0;
  const secLineEndX = isHorizontal ? lineLength : xSecondary;
  const secLineEndY = isHorizontal ? ySecondary : lineLength;

  if (!enabled) return null;
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={0} y={0} width={width} height={height} />
        </clipPath>
      </defs>

      {/* No transform translation; positions are computed from the live domain */}
      <g clipPath={`url(#${clipId})`}>
        {/* Secondary axis line */}
        <line
          x1={secLineStartX}
          y1={secLineStartY}
          x2={secLineEndX}
          y2={secLineEndY}
          stroke={themeColors.secondaryAxis}
          strokeWidth={3}
          className="secondary-axis-line"
        />

        {/* Minor ticks - with labels for those that have showLabel */}
        {minorTicks.map((tick) => {
          // IMPORTANT: map world→screen using the live visible domain so scaling matches primary
          const tickPos = valueToPixel(tick.value);
          const tickLevel = tick.level ?? 3;
          // Tick height based on level
          const tickHeight = tickLevel === 1 ? 10 : tickLevel === 2 ? 8 : 6;
          const x1 = isHorizontal ? tickPos : xSecondary - tickHeight / 2;
          const y1 = isHorizontal ? ySecondary - tickHeight / 2 : tickPos;
          const x2 = isHorizontal ? tickPos : xSecondary + tickHeight / 2;
          const y2 = isHorizontal ? ySecondary + tickHeight / 2 : tickPos;
          
          // Check if this tick should show a label
          const valueKey = `secondary-${tick.value}`;
          const individualHidden = labelVisibility[valueKey]?.hidden === true;
          const individualShown = labelVisibility[valueKey]?.hidden === false;
          const isLabelVisible = hideAllLabels ? individualShown : !individualHidden;
          const shouldShowLabel = tick.showLabel && tick.label && isLabelVisible;
          
          // Label positions
          const labelX = isHorizontal ? tickPos : xSecondary + 20;
          let labelY = isHorizontal ? ySecondary - 25 : tickPos;
          if (isHorizontal && labelY < 12) {
            labelY = ySecondary + 25;
          }
          
          const label = shouldShowLabel ? getLabel(tick.value) : '';
          const needsKatex = numberType === 'fraction' || label.includes('\\');
          
          return (
            <g key={`secondary-minor-${tick.value}`} className="secondary-minor-tick-group">
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={themeColors.secondaryTickLight}
                strokeWidth={tickLevel === 1 ? 2 : tickLevel === 2 ? 1.5 : 1}
                className="secondary-minor-tick"
              />
              {/* Label for minor ticks with showLabel */}
              {shouldShowLabel && (
                <g>
                  <rect
                    x={isHorizontal ? labelX - minorLabelBoxWidth / 2 : labelX - 10}
                    y={isHorizontal ? labelY - minorLabelBoxHeight / 2 : labelY - minorLabelBoxHeight / 2}
                    width={isHorizontal ? minorLabelBoxWidth : minorLabelBoxWidth}
                    height={minorLabelBoxHeight}
                    fill="transparent"
                    className="secondary-label-click-area"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => onLabelClick(valueKey, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  {needsKatex ? (
                    <foreignObject
                      x={isHorizontal ? labelX - minorLabelBoxWidth / 2 : labelX}
                      y={isHorizontal ? labelY - minorLabelBoxHeight / 2 : labelY - minorLabelBoxHeight / 2}
                      width={minorLabelBoxWidth}
                      height={minorLabelBoxHeight}
                      style={{ pointerEvents: 'none' }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isHorizontal ? 'center' : 'flex-start',
                        width: '100%',
                        height: '100%',
                        fontSize: `${14 * textScale}px`,
                        fontWeight: 500,
                        color: themeColors.secondaryLabel
                      }}>
                        <MathRenderer expression={label} />
                      </div>
                    </foreignObject>
                  ) : (
                    <text
                      x={labelX}
                      y={labelY}
                      textAnchor={isHorizontal ? 'middle' : 'start'}
                      dominantBaseline={isHorizontal ? 'auto' : 'middle'}
                      fontSize={14 * textScale}
                      fontWeight="500"
                      fill={themeColors.secondaryLabel}
                      className="secondary-minor-tick-label"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {label}
                    </text>
                  )}
                </g>
              )}
              {/* Hidden label placeholder for minor ticks */}
              {tick.showLabel && tick.label && !isLabelVisible && (
                <g
                  className="label-placeholder-group"
                  onClick={(e) => onLabelClick(valueKey, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    x={isHorizontal ? labelX - 12 : xSecondary + 5}
                    y={isHorizontal ? labelY - 12 : labelY - 8}
                    width="24"
                    height="16"
                    fill="transparent"
                    className="label-click-area"
                  />
                  <circle
                    cx={labelX}
                    cy={labelY}
                    r="5"
                    fill={themeColors.secondaryPlaceholderFill}
                    stroke={themeColors.secondaryPlaceholderStroke}
                    strokeWidth="1"
                    strokeDasharray="2,2"
                    className="secondary-label-placeholder minor"
                  />
                </g>
              )}
            </g>
          );
        })}

        {/* Major ticks with labels */}
        {majorTicks.map((tick) => {
          // IMPORTANT: map world→screen using the live visible domain so scaling matches primary
          const tickPos = valueToPixel(tick.value);
          const tickHeight = 12;
          const x1 = isHorizontal ? tickPos : xSecondary - tickHeight / 2;
          const y1 = isHorizontal ? ySecondary - tickHeight / 2 : tickPos;
          const x2 = isHorizontal ? tickPos : xSecondary + tickHeight / 2;
          const y2 = isHorizontal ? ySecondary + tickHeight / 2 : tickPos;

          const labelX = isHorizontal ? tickPos : xSecondary + 20;
          let labelY = isHorizontal ? ySecondary - 35 : tickPos;
          if (isHorizontal) {
            // Prevent clipping when secondary line is near the top
            if (labelY < 12) {
              labelY = ySecondary + 35;
            }
          }

          const valueKey = `secondary-${tick.value}`;
          const individualHidden = labelVisibility[valueKey]?.hidden === true;
          const individualShown = labelVisibility[valueKey]?.hidden === false;
          const isLabelVisible = hideAllLabels ? individualShown : !individualHidden;

          const label = getLabel(tick.value);
          const needsKatex = numberType === 'fraction' || label.includes('\\');

          return (
            <g key={`secondary-major-${tick.value}`} className="secondary-major-tick">
              {/* Tick mark */}
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={themeColors.secondaryTick} strokeWidth={2} className="secondary-tick-mark" />

              {/* Alignment line: connects primary axis to secondary axis at tick position */}
              <line
                x1={isHorizontal ? tickPos : lineX}
                y1={isHorizontal ? lineY : tickPos}
                x2={isHorizontal ? tickPos : xSecondary}
                y2={isHorizontal ? ySecondary : tickPos}
                stroke={themeColors.alignmentLine}
                strokeWidth={1}
                strokeDasharray="2,2"
                className="alignment-line"
              />

              {/* Label */}
              {isLabelVisible ? (
                <g>
                  <rect
                    x={isHorizontal ? labelX - majorLabelBoxWidth / 2 : labelX - 10}
                    y={isHorizontal ? labelY - majorLabelBoxHeight / 2 : labelY - majorLabelBoxHeight / 2}
                    width={majorLabelBoxWidth}
                    height={majorLabelBoxHeight}
                    fill="transparent"
                    className="secondary-label-click-area"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => onLabelClick(valueKey, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  {needsKatex ? (
                    <foreignObject
                      x={isHorizontal ? labelX - majorLabelBoxWidth / 2 : labelX}
                      y={isHorizontal ? labelY - majorLabelBoxHeight / 2 : labelY - majorLabelBoxHeight / 2}
                      width={majorLabelBoxWidth}
                      height={majorLabelBoxHeight}
                      style={{ pointerEvents: 'none' }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isHorizontal ? 'center' : 'flex-start',
                        width: '100%',
                        height: '100%',
                        fontSize: `${16 * textScale}px`,
                        fontWeight: 700,
                        color: themeColors.secondaryLabel
                      }}>
                        <MathRenderer expression={label} />
                      </div>
                    </foreignObject>
                  ) : (
                    <text
                      x={labelX}
                      y={labelY}
                      textAnchor={isHorizontal ? 'middle' : 'start'}
                      dominantBaseline={isHorizontal ? 'auto' : 'middle'}
                      fontSize={16 * textScale}
                      fontWeight="bold"
                      fill={themeColors.secondaryLabel}
                      className="secondary-tick-label"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {label}
                    </text>
                  )}
                </g>
              ) : (
                <>
                  <rect
                    x={isHorizontal ? labelX - 20 : xSecondary}
                    y={isHorizontal ? labelY - 10 : labelY - 20}
                    width={40}
                    height={20}
                    fill="transparent"
                    className="secondary-label-click-area"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => onLabelClick(valueKey, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  <circle
                    cx={labelX}
                    cy={labelY}
                    r={10}
                    fill={themeColors.secondaryPlaceholderFill}
                    stroke={themeColors.secondaryPlaceholderStroke}
                    strokeWidth={1}
                    strokeDasharray="2,2"
                    className="secondary-label-placeholder"
                    style={{ pointerEvents: 'none' }}
                  />
                </>
              )}
            </g>
          );
        })}
      </g>
    </g>
  );
});

SecondaryLineLayer.displayName = 'SecondaryLineLayer';

export default SecondaryLineLayer;


